import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, Navigation, Phone } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import BookingForm from "@/components/BookingForm";
import { ROUTES } from "@/data/site";
import { useSite, telLink, waLink } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";

export default function RoutePage() {
  const { slug } = useParams();
  const s = useSite();
  const route = ROUTES.find((r) => r.slug === slug);
  if (!route) return <NotFound />;

  const title = `${route.from} to ${route.to} Taxi`;
  const others = ROUTES.filter((r) => r.slug !== slug).slice(0, 8);

  return (
    <>
      <Seo title={`${title} | Book ${route.from} ${route.to} Cab | Chouhan Taxi Service`}
        description={route.intro} image={route.image} path={`/routes/${slug}`} />
      <Breadcrumbs items={[{ label: "Routes" }, { label: title }]} />
      <section className="relative h-72 sm:h-80">
        <img src={route.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-x h-full flex flex-col justify-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-white flex flex-wrap items-center gap-3">
            {route.from} <ArrowRight className="w-7 h-7 text-brand-sky" /> {route.to} Taxi
          </h1>
          <div className="mt-5 flex flex-wrap gap-4 text-white/90 text-sm">
            <span className="flex items-center gap-2"><Navigation className="w-4 h-4 text-brand-sky" /> {route.distance}</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-sky" /> {route.time} drive</span>
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p className="text-lg text-slate-600 leading-relaxed">{route.intro}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{route.content}</p>

            <div className="mt-8 bg-brand-light rounded-2xl p-6 border border-brand/10">
              <h2 className="font-heading font-bold text-navy-heading text-xl mb-2">Book your {route.from} to {route.to} taxi</h2>
              <p className="text-slate-600 text-sm mb-4">Choose a sedan, SUV or tempo traveller. One-way and round-trip trips available.</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-royal transition-colors">Book Now <ArrowRight className="w-4 h-4" /></Link>
                <a href={telLink(s)} className="inline-flex items-center gap-2 border border-slate-300 text-navy font-semibold px-6 py-3 rounded-full"><Phone className="w-4 h-4" /> Call Now</a>
                <a href={waLink(s, `Hi, I want a ${route.from} to ${route.to} taxi.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-whatsapp text-white font-semibold px-6 py-3 rounded-full"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</a>
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-heading font-bold text-navy-heading text-xl mb-4">Other Popular Routes</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {others.map((r) => (
                  <Link key={r.slug} to={`/routes/${r.slug}`} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-brand transition-colors group">
                    <span className="text-sm font-medium text-navy-heading">{r.from} to {r.to} Taxi</span>
                    <ArrowRight className="w-4 h-4 text-brand group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-lg p-6">
              <h2 className="font-heading font-bold text-navy-heading text-lg mb-4">Book This Taxi</h2>
              <BookingForm compact defaults={{ pickup_location: route.from, drop_location: route.to, trip_type: "One Way" }} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
